import { useData, useDataAuth } from "../../runtime/provider";
import { trpc, RouterOutputs } from "@utils/trpc";
import { captureException } from "@utils/captureException";
import { getQueryKey } from "@trpc/react-query";
import type { z } from "zod";
import { forYouPageWireSchema } from "@libs/wire";
import { infiniteDataSchema } from "@utils/durableCache/schemas";
import { makeOnUserChange } from "@hooks/useOnUserChange";

// Clear + refetch ONLY on a real login/logout transition (see the factory's
// doc comment — the old inline useEffect refired on every screen mount).
const useOnForYouUserChange = makeOnUserChange();

const FOR_YOU_INITIAL_LIMIT = 6;
export const FOR_YOU_PAGE_LIMIT = 3;

export const FOR_YOU_INPUTS = {
  initialLimit: FOR_YOU_INITIAL_LIMIT,
  limit: FOR_YOU_PAGE_LIMIT,
};

type InfiniteForYouQuery = ReturnType<typeof useForYou>;
export type ForYouData =
  InfiniteForYouQuery["data"]["pages"][number]["data"][number];

type ForYouPage = RouterOutputs["catalog"]["listForYouPaginated"];

// One-way drift check: server response shape must be assignable to
// `z.infer<schema>` so legitimate fresh data parses successfully. The
// reverse direction (schema infer → RouterOutput) is intentionally NOT
// asserted because the schema uses `.passthrough()` on item entities to
// allow server-internal fields beyond what the strict client shape
// declares (which adds an `[k: string]: unknown` index signature to the
// inferred type). See `functions/api/types/recommendations.ts` for the
// rationale.
type _ForYouPageWireSchemaCheck =
  ForYouPage extends z.infer<typeof forYouPageWireSchema> ? true : never;
const _forYouPageWireSchemaDriftCheck: _ForYouPageWireSchemaCheck = true;
void _forYouPageWireSchemaDriftCheck;

const forYouInfiniteSchema = infiniteDataSchema(
  forYouPageWireSchema,
  // pageParams: cursor is an opaque `string` (finding 9) — legacy `number`
  // cursors from pre-fix cached pages are also accepted. Initial fetch starts
  // at undefined.
  forYouPageWireSchema.shape.endCursor,
);

/*
 * Get the list of recommendations albums and playlists for the current user. Overrides the default staleTime to 1 day, so every day the user will get the latest recommendations. Also sets catalog caches. This means the information fetched here can then be opened in the collection screen, and the data doesn't need to be fetched again.
 */
export const useForYou = (options?: any) => {
  const data = useData();
  const { firebaseUser } = useDataAuth();
  const queryKey = getQueryKey(
    trpc.catalog.listForYouPaginated,
    FOR_YOU_INPUTS,
    "infinite",
  );
  const [, infiniteQuery] =
    trpc.catalog.listForYouPaginated.useSuspenseInfiniteQuery(FOR_YOU_INPUTS, {
      getNextPageParam: (lastPage: { endCursor?: string | number }) =>
        lastPage.endCursor,
      staleTime: 60 * 60 * 1000, // 1 hour — recommendations refresh hourly
      // gcTime must outlive staleTime or every re-mount after the default
      // 5-min gc discards the cache and refetches — the engine records each
      // serve, so navigation churn burned the anti-repeat pool (2026-08-03).
      gcTime: 60 * 60 * 1000,
      // NOTE: not opted into structural-sharing (`meta.decompose`) yet.
      // Items are a mixed (playlist | album) discriminated union. A partial
      // decompose into `cached_albums` would be possible (filter to
      // `type:"album"`), but a `cached_playlists` table doesn't exist yet,
      // so we'd be only half-warming the cache. Revisit once that table
      // lands — see `useContentByGenre` for the multi-shape filter pattern.
      ...data.cache.durableInfiniteOptions<ForYouPage>(
        queryKey,
        forYouInfiniteSchema,
      ),
      ...options,
    });

  // Undefined until Firebase reports, null once it reports signed out.
  useOnForYouUserChange(firebaseUser && firebaseUser.uid, () => {
    data.utils.catalog.listForYouPaginated.setInfiniteData(
      FOR_YOU_INPUTS,
      undefined,
    );
    data.utils.catalog.listForYouPaginated.refetch().catch((error) => {
      captureException(error, {
        channel: "breadcrumb",
        area: "query",
        operation: "refetch-for-you-after-user-change",
      });
    });
  });

  return infiniteQuery;
};
